import EntityRequest, { Provenance } from '../store/schema/EntityRequest';
import ExecuteSuggestionsRequest from '../store/schema/ExecuteSuggestionsRequest';
import isUserBoundToLinkedIn from '../utils/isUserBoundToLinkedIn';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FeedbackManagerState } from 'owa-feedback-manager/lib/store/schema/FeedbackManagerState';
import { getGuid } from 'owa-guid';
import { makeGetRequest, makePostRequest } from 'owa-ows-gateway';
import { SubstrateSearchSuggestionsResponse } from 'owa-search-service';
import { getDefaultAnchorMailboxCookie } from 'owa-service/lib/anchormailbox';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { SUBSTRATE_SUGGESTION_FIELDS } from 'owa-substrate-people-suggestions/lib/utils/substrateSuggestionUtils';
import { getAccessTokenforResource, getDelegationTokenForOwa } from 'owa-tokenprovider';
import { trace } from 'owa-trace';

const INIT_GET_ENDPOINT = '/search/api/v1/init?';
const ENTERPRISE_TOKEN_RESOURCE = 'https://outlook.office365.com/search';
const CONSUMER_TOKEN_SCOPE = 'https://substrate.office.com/SubstrateSearch-Internal.ReadWrite';
const SUGGESTIONS_POST_ENDPOINT = '/search/api/v1/suggestions?';
const SCENARIO_NAME = 'owa.react.compose';
const MAX_ENTRIES_DIRECTORY = '20';
const MAX_ENTRIES_TYPEDOWN = '5';

export default async function substrateSuggestions(
    queryString: string,
    includeDirectory: boolean,
    feedbackManager?: FeedbackManagerState,
    includeImplicitGroups?: boolean,
    currentRecipients?: string[]
): Promise<SubstrateSearchSuggestionsResponse> {
    if (queryString == '') {
        return makeInitRequest();
    } else {
        let entityRequest = await createEntityRequest(
            queryString,
            includeDirectory,
            currentRecipients
        );
        let suggestionJsonRequest = createSuggestionsJsonRequest(
            entityRequest,
            feedbackManager && feedbackManager.conversationId
        );

        return makePostRequest(
            SUGGESTIONS_POST_ENDPOINT + getParams(),
            suggestionJsonRequest,
            undefined /*correlationId*/,
            false /*returnFullResponse*/,
            await getCustomHeaders(feedbackManager && feedbackManager.clientSessionId),
            undefined /* throwServiceError */,
            undefined /* sendPayloadAdBody */,
            /* 3S does not need auth cookies so omitting them will decrease request header size */
            false /* includecredentials */
        )
            .then(resp => {
                return resp;
            })
            .catch(err => {
                trace.error(err);
                return null;
            });
    }
}

async function makeInitRequest() {
    if (
        isConsumer() &&
        isFeatureEnabled('rp-linkedInPeopleSuggestions') &&
        isUserBoundToLinkedIn()
    ) {
        const requestInit: RequestInit = {};
        requestInit.headers = new Headers();
        const token = await getDelegationTokenForOwa(CONSUMER_TOKEN_SCOPE);
        (<Headers>requestInit.headers).set('X-User-Context', 'litoken=bearer');
        (<Headers>requestInit.headers).set('Authorization', `Bearer ${token}`);
        const defaultAnchorMailbox = getDefaultAnchorMailboxCookie();
        if (defaultAnchorMailbox) {
            (<Headers>requestInit.headers).append('X-AnchorMailbox', defaultAnchorMailbox);
        }
        requestInit.method = 'GET';

        const response: Response = await fetch(
            window.location.origin + INIT_GET_ENDPOINT + getParams(),
            requestInit
        );

        if (response && response.status == 200 /* OK */) {
            return response;
        } else {
            trace.error('makeInitRequest: Unsuccessful response status');
            return null;
        }
    }
    return makeGetRequest(
        INIT_GET_ENDPOINT + getParams(),
        undefined /*correlationId*/,
        true /*returnFullResponse*/,
        await getCustomHeaders(),
        undefined /* throwServiceError */,
        /* 3S does not need auth cookies so omitting them will decrease request header size */
        false /* IncludeCredentials */
    )
        .then(resp => {
            return resp.json() as Promise<SubstrateSearchSuggestionsResponse>;
        })
        .catch(err => {
            trace.error(err);
            return null;
        });
}

async function createEntityRequest(
    queryString: string,
    includeDirectory: boolean,
    currentRecipients?: string[]
): Promise<EntityRequest> {
    const isLinkedInEnabled =
        isFeatureEnabled('rp-linkedInPeopleSuggestions') && isUserBoundToLinkedIn();
    let entityRequest: EntityRequest = {
        Query: {
            QueryString: queryString,
        },
        EntityType: 'People',
        Provenances: getProvenances(includeDirectory, isLinkedInEnabled),
        Size: includeDirectory ? MAX_ENTRIES_DIRECTORY : MAX_ENTRIES_TYPEDOWN,
    };

    if (currentRecipients && currentRecipients.length > 0) {
        entityRequest.Context = createEntityRequestContext(currentRecipients);
    }

    entityRequest.Fields = SUBSTRATE_SUGGESTION_FIELDS;

    return entityRequest;
}

function createSuggestionsJsonRequest(
    entityRequest: EntityRequest,
    cvid: string
): ExecuteSuggestionsRequest {
    return {
        AppName: 'OWA',
        Scenario: {
            Name: SCENARIO_NAME,
        },
        Cvid: cvid ? cvid : getGuid(),
        EntityRequests: [entityRequest],
    };
}

function createEntityRequestContext(currentRecipients: string[]): { [index: string]: string } {
    let context: { [index: string]: string } = {};

    if (currentRecipients) {
        context.RecipientsTo = currentRecipients.toString();
    }

    return context;
}

function getProvenances(includeDirectory: boolean, isLinkedInEnabled: boolean): Provenance[] {
    let provenances: Provenance[] = ['Mailbox'];

    if (includeDirectory) {
        provenances.push('Directory');
    }

    if (isLinkedInEnabled) {
        provenances.push('LinkedIn');
    }

    return provenances;
}

function getParams(): string {
    const params = {
        scenario: SCENARIO_NAME,
    };

    return Object.keys(params)
        .map(key => `${key}=${params[key]}`)
        .join('&');
}

async function getCustomHeaders(sessionId?: string): Promise<any> {
    let headers: { [header: string]: any } = {};
    if (sessionId) {
        headers['client-session-id'] = sessionId;
    }

    if (isFeatureEnabled('rp-linkedInPeopleSuggestions') && isUserBoundToLinkedIn()) {
        if (isConsumer()) {
            headers['X-User-Context'] = 'litoken=bearer';
        } else {
            const linkedInToken = await getAccessTokenforResource(ENTERPRISE_TOKEN_RESOURCE);
            headers['X-User-Context'] = `litoken=${linkedInToken}`;
        }
    }
    return headers;
}
